/**
 * Putting sizes in the order a person expects.
 *
 * The database returns sizes alphabetically, which turns a letter scale into
 * "L, M, S, XL, XS, XXL" and a kids scale into "10Y, 12Y, 14Y, 2Y". On a list
 * that is merely untidy; on a grid where the size is the row label it is
 * unreadable, so the receiving screen sorts with this instead.
 *
 * Letter sizes have a known running order. Everything else - shoe sizes, waist
 * measurements, ages - is ordered by the number in it, so "2Y" comes before
 * "10Y" and "36" before "40". Anything left over falls back to a natural sort,
 * which is still better than plain alphabetical.
 */

const LETTERS = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];

function rank(size: string): number | null {
  const k = size.trim().toLowerCase().replace(/[\s.-]/g, '');
  const direct = LETTERS.indexOf(k);
  if (direct >= 0) return direct;
  // "2XL" and "3XL" are the same thing as "XXL" and "XXXL".
  const m = /^(\d)x(l|s)$/.exec(k);
  if (m) {
    const spelled = 'x'.repeat(Number(m[1])) + m[2];
    const i = LETTERS.indexOf(spelled);
    if (i >= 0) return i;
  }
  return null;
}

export function compareSizes(a: string, b: string): number {
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== null && rb !== null) return ra - rb;
  if (ra !== null) return -1;
  if (rb !== null) return 1;

  const na = Number.parseFloat(a);
  const nb = Number.parseFloat(b);
  const aNum = !Number.isNaN(na);
  const bNum = !Number.isNaN(nb);
  if (aNum && bNum && na !== nb) return na - nb;
  if (aNum !== bNum) return aNum ? -1 : 1;

  return a.localeCompare(b, undefined, { numeric: true });
}
